import { useEffect, useId, useRef, useState } from "react";

import {
  UNSPECIFIED_BREED,
  UNSPECIFIED_BREED_LABEL,
} from "../../lib/breeds";

type Props = {
  breeds: string[];
  hasUnspecified?: boolean;
  selected: string[];
  onChange: (next: string[]) => void;
  testId?: string;
};

export function BreedMultiSelect({
  breeds,
  hasUnspecified = false,
  selected,
  onChange,
  testId = "breed-multi-select",
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const options = [
    ...breeds,
    ...(hasUnspecified ? [UNSPECIFIED_BREED] : []),
  ];

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function toggle(value: string) {
    onChange(
      selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value],
    );
  }

  function labelFor(value: string) {
    return value === UNSPECIFIED_BREED ? UNSPECIFIED_BREED_LABEL : value;
  }

  function buttonLabel() {
    if (options.length === 0) {
      return "Brak ras w stadzie";
    }
    if (selected.length === 0) {
      return "Wszystkie rasy";
    }
    if (selected.length === 1) {
      return labelFor(selected[0]!);
    }
    return `${selected.length} rasy wybrane`;
  }

  return (
    <div className="breed-dropdown" ref={rootRef} data-testid={testId}>
      {options.length === 0 ? (
        <p className="muted breed-dropdown__empty">
          Brak zdefiniowanych ras w stadzie — grupa obejmie wszystkie zwierzęta.
        </p>
      ) : (
        <>
          <div className="breed-dropdown__control">
            <button
              type="button"
              className="breed-dropdown__trigger input"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listId}
              data-testid={`${testId}-trigger`}
              onClick={() => setOpen((value) => !value)}
            >
              <span className="breed-dropdown__label">{buttonLabel()}</span>
              <span className="breed-dropdown__chevron" aria-hidden="true" />
            </button>
            {open ? (
              <ul
                id={listId}
                className="breed-dropdown__panel"
                role="listbox"
                aria-multiselectable="true"
                data-testid={`${testId}-panel`}
              >
                {options.map((breed) => (
                  <li key={breed}>
                    <label
                      className={
                        selected.includes(breed)
                          ? "breed-dropdown__option breed-dropdown__option--picked"
                          : "breed-dropdown__option"
                      }
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(breed)}
                        onChange={() => toggle(breed)}
                      />
                      <span>{labelFor(breed)}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          {open ? null : (
            <p className="muted breed-dropdown__hint">
              {selected.length === 0
                ? "Bez wyboru — grupa obejmie wszystkie rasy."
                : `Wybrane: ${selected.map(labelFor).join(", ")}`}
            </p>
          )}
        </>
      )}
    </div>
  );
}
